import { DEFAULT_PUMP_CONFIG, formatPumpDuration, pumpDurationSeconds } from "utils/pumpFormat";
import { MAX_WATER_EVENTS, emptyWaterEvent, plannedLevels } from "utils/waterEvents";

/**
 * Scheduled pump operations for an experiment: "at minute N, fill/drain X cm". Relative changes, not
 * target levels; the backend runs them with a camera photo before and after each one.
 *
 * Controlled: rows hold what the inputs hold (strings while typing). The parent validates
 * (utils/waterEvents.js) and passes `rowErrors` back so each problem shows on its own row.
 *
 * Props:
 *   events       - [{at_minute, action, cm}]
 *   onChange     - (events) => void
 *   config       - the backend's pump_config (seconds per cm, limits)
 *   rowErrors    - (string|null)[] one per row
 *   startLevelCm - tank level at the start (shows the projected level after each event when set)
 *   disabled     - read-only (e.g. while a request is in flight)
 */
export default function WaterEventsEditor({
  events,
  onChange,
  config = DEFAULT_PUMP_CONFIG,
  rowErrors = [],
  startLevelCm,
  disabled = false,
}) {
  const update = (index, patch) =>
    onChange(events.map((event, i) => (i === index ? { ...event, ...patch } : event)));
  const remove = (index) => onChange(events.filter((_, i) => i !== index));
  const add = () => {
    // Suggest a time after the last event so a new row is valid at a glance.
    const last = events[events.length - 1];
    const suggested = last ? Math.ceil(Number(last.at_minute) + (Number(last.cm) * (config.seconds_per_cm ?? DEFAULT_PUMP_CONFIG.seconds_per_cm) + (config.event_overhead_seconds ?? DEFAULT_PUMP_CONFIG.event_overhead_seconds)) / 60) : 10;
    onChange([...events, { ...emptyWaterEvent(), at_minute: String(Number.isFinite(suggested) ? suggested : 10) }]);
  };

  const levels = Number.isFinite(Number(startLevelCm)) && startLevelCm !== "" ? plannedLevels(events, startLevelCm) : [];

  return (
    <div className="flex flex-col gap-2" data-testid="water-events-editor">
      <div>
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Water events</span>
        <p className="mt-0.5 text-xs text-slate-500">
          Pump operations at set minutes after the run starts. Each one is photographed before and
          after. They change the level by the distance given, they do not aim for a target level.
        </p>
      </div>

      {events.length === 0 && (
        <p className="text-xs text-slate-500">No water events - the pump will only run if you use Fill/Drain by hand.</p>
      )}

      {events.map((event, index) => {
        const cm = Number(event.cm);
        const minute = Number(event.at_minute);
        const hasTimes = Number.isFinite(cm) && Number.isFinite(minute) && event.cm !== "" && event.at_minute !== "";
        const label = `Water event ${index + 1}`;
        return (
          <div
            key={index}
            className="rounded-xl border border-slate-200 bg-slate-50 p-2"
            data-testid={`water-event-row-${index}`}
          >
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 sm:items-end">
              <label className="text-xs font-semibold text-slate-600">
                At minute
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={event.at_minute}
                  disabled={disabled}
                  aria-label={`${label} minute`}
                  onChange={(e) => update(index, { at_minute: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm"
                />
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Action
                <select
                  value={event.action}
                  disabled={disabled}
                  aria-label={`${label} action`}
                  onChange={(e) => update(index, { action: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm"
                >
                  <option value="fill">Fill</option>
                  <option value="drain">Drain</option>
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Distance (cm)
                <input
                  type="number"
                  min={config.min_cm_per_op}
                  max={config.max_cm_per_op}
                  step="0.1"
                  value={event.cm}
                  disabled={disabled}
                  aria-label={`${label} distance (cm)`}
                  onChange={(e) => update(index, { cm: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm"
                />
              </label>
              <p className="text-xs text-slate-600 sm:pb-2" data-testid={`water-event-timing-${index}`}>
                {hasTimes ? (
                  <>
                    Pumps {formatPumpDuration(pumpDurationSeconds(cm, config))}
                    {levels[index] !== undefined && !Number.isNaN(levels[index]) ? `, level then ${levels[index]} cm` : ""}
                  </>
                ) : (
                  "—"
                )}
              </p>
              <button
                type="button"
                onClick={() => remove(index)}
                disabled={disabled}
                className="rounded-lg border border-red-200 bg-white px-2 py-1.5 text-xs font-bold text-red-700 disabled:opacity-50"
              >
                Remove
              </button>
            </div>
            {rowErrors[index] && (
              <p role="alert" className="mt-1 text-xs text-red-700" data-testid={`water-event-error-${index}`}>
                {rowErrors[index]}
              </p>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={add}
        disabled={disabled || events.length >= MAX_WATER_EVENTS}
        className="self-start rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 disabled:opacity-50"
      >
        + Add water event
      </button>
    </div>
  );
}
